import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast, Slide } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import {
  parentalApprovalSchema,
  type ParentalApprovalFormData,
} from '../validation/parental-approval.schemas';
import { usersApi } from '../api/users.api';
import { useAuthProviderV2, useRoutesProvider } from '@/providers/provider-hooks';
import { updateFailureToast } from '@/toast';
import { delay, trimStringsFromAnywhere } from '@/shared/utils';
import { getErrorMessage } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';
import { resolvePostAuthTarget } from '../../auth/utils/resolve-post-auth-target';
import { trackParentalConsentSubmitted } from '@/telemetry/posthog-events';

/**
 * Custom hook for parental approval form using react-hook-form + Zod
 *
 * Features:
 * - Client-side validation with Zod (8 required checkboxes + signature)
 * - Token refresh after successful submission
 * - Automatic navigation to user's home page
 * - Backend error mapping to form fields
 * - Loading states during submission
 *
 * Usage:
 * ```tsx
 * const { form, onSubmit, isSubmitting } = useParentalApprovalWithForm();
 * <Form {...form}>
 *   <form onSubmit={form.handleSubmit(onSubmit)}>
 *     <Controller name="parentApproval" control={form.control} ... />
 *   </form>
 * </Form>
 * ```
 */
export function useParentalApprovalWithForm() {
  const navigate = useNavigate();
  const { PATHS, PAGES, ROLES, getUserHomePage } = useRoutesProvider();
  const { handleTokenRefresh } = useAuthProviderV2();
  const [isNavigating, setIsNavigating] = useState(false);

  // Guard to prevent duplicate success navigation
  const hasNavigated = useRef(false);

  // Guard to prevent duplicate submission
  const isSubmittingRef = useRef(false);

  // Initialize form with Zod validation
  const form = useForm<ParentalApprovalFormData>({
    resolver: zodResolver(parentalApprovalSchema),
    defaultValues: {
      parentApproval: false,
      prinPrezentaConfirmamInscriereaMinorului: false,
      ecoticPoateFolosiImagini: false,
      amFostInformati: false,
      prinPrezentaConfirmCaAmCititInformarea: false,
      confirmCaInCazulInCareProiectul: false,
      consimtPentruPastrareaDatelorPersonale: false,
      amCititSiSuntDeAcord: false,
      signature: '',
    },
  });

  // Save signature mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ParentalApprovalFormData) => {
      const trimmedData = trimStringsFromAnywhere(data);
      return usersApi.updateSignature(trimmedData as any);
    },
    onSuccess: async () => {
      await delay(500);
      // Success toast will be shown by useEffect after form reset
      // Navigation handled by useEffect watching isSubmitSuccessful
    },
    onError: (error) => {
      console.error('Parental approval save error:', error);

      // Detect "already submitted" error - treat as success by triggering form reset
      if (error?.message?.includes('resource.already.processed') ||
          error?.message?.includes('Semnatura parentala')) {
        trackParentalConsentSubmitted();
        toast.success('Acordul parental a fost deja salvat.', { autoClose: 2000 });
        // Reset form to trigger isSubmitSuccessful and navigation
        form.reset(form.getValues(), { keepValues: true });
        return; // Don't show error
      }

      // Map backend errors to form fields
      applyServerFieldErrors(error, form.setError);
    },
  });

  // Handle successful submission with proper form reset before navigation
  // This ensures isDirty is false when navigate() is called, preventing the navigation blocker
  useEffect(() => {
    const handleSuccessNavigation = async () => {
      // Guard: Prevent duplicate runs even if React re-renders
      if (hasNavigated.current) {
        return;
      }

      if (saveMutation.isSuccess) {
        hasNavigated.current = true;  // Set IMMEDIATELY before any async work
        setIsNavigating(true);
        trackParentalConsentSubmitted();

        // Refresh token to update user's signature status
        const newToken = await handleTokenRefresh(true);
        if (newToken) {
          // Decode the new token locally
          const decodedUserInfo = jwtDecode(newToken.toString()) as any;

          // Reset form BEFORE navigation to clear isDirty flag
          // Valorile curente devin noile defaultValues, deci isDirty devine false.
          // NU folosi `reset(undefined, { keepValues: true })`: cu valori omise,
          // react-hook-form pastreaza defaultValues-urile INITIALE si recalculeaza
          // isDirty prin _getDirty() -> ramane true dupa o salvare reusita, iar
          // navigarea de mai jos loveste blocker-ul din useFormNavigation
          // (dialogul "Modificari nesalvate" dupa succes, defect T7/D5).
          form.reset(form.getValues());

          // Show success message while waiting for state update
          toast.success('Acordul a fost salvat.', {
            autoClose: 2000
          });

          // Wait for React to process auth context state update
          // handleTokenRefresh calls setToken() which schedules a state update
          // We need to give React time to:
          // 1. Process setToken() state update
          // 2. Re-run userInfo useMemo with new token
          // 3. Update context value
          await delay(300);  // Increased from 100ms to 300ms for state batching

          // FE-M7: `resolvePostAuthTarget`, sursa unica de adevar (CLAUDE.md pct. 8), nu un
          // "home" hardcodat -- daca tokenul reimprospatat inca poarta `isEnabled: false`
          // (intarziere de backend), resolverul il tine pe pagina de acord, nu-l trimite spre
          // home ca sa fie apoi intors de `AuthenticatedLayout` (flash + du-te-vino).
          const targetPath =
            resolvePostAuthTarget(decodedUserInfo, null, {
              PATHS,
              PAGES,
              ROLES,
              getUserHomePage,
            }) ?? getUserHomePage(decodedUserInfo['role']);

          // Navigate after form is reset (isDirty will be false now)
          navigate(targetPath);
        } else if (import.meta.env.DEV) {
          console.error('[ParentalApproval] Token refresh returned undefined/null');
        }
        // Note: No need to set isNavigating false - component unmounts after navigate
      }
    };

    handleSuccessNavigation();
  }, [saveMutation.isSuccess]);

  // Form submission handler
  const onSubmit = async (data: ParentalApprovalFormData) => {
    // Guard: Prevent duplicate submission
    if (isSubmittingRef.current) {
      console.warn('[ParentalApproval] ❌ Duplicate submission prevented');
      return;
    }

    isSubmittingRef.current = true;
    const loadError = 'Acordul parental nu s-a putut salva.';
    const id = toast.loading('Acordul parental se salveaza...', { transition: Slide });

    try {
      console.log('[ParentalApproval] Calling backend API...');
      await saveMutation.mutateAsync(data);
      console.log('[ParentalApproval] ✅ Backend API success');
      toast.dismiss(id);
    } catch (error: unknown) {
      console.error('[ParentalApproval] ❌ Backend API error:', error);
      const message = getErrorMessage(error, '');
      if (message) {
        updateFailureToast(message, id);
      } else {
        updateFailureToast(loadError, id);
      }
    } finally {
      isSubmittingRef.current = false;
      console.log('[ParentalApproval] Submission complete, isSubmittingRef reset');
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting: saveMutation.isPending || isNavigating, // Combine states to prevent double-submission
    isDirty: form.formState.isDirty, // Track unsaved changes for navigation blocking
  };
}
